import { Badge, Button, Card, Spinner } from '@fc/ui'
import { useQuery } from '@tanstack/react-query'
import { CalendarDays, CalendarPlus, MapPin, Users } from 'lucide-react'
import { Link } from 'react-router-dom'
import { PageHeader } from '../../components/page-header'
import { listEvents, type EventDto } from '../../lib/events-api'

const kindVariant: Record<string, 'primary' | 'secondary' | 'gold' | 'neutral'> = {
  service: 'primary',
  gathering: 'secondary',
  class: 'gold',
  registration: 'gold',
  camp: 'neutral',
}

function formatWhen(e: EventDto): string {
  if (!e.startsAt) return 'Unscheduled'
  const d = new Date(e.startsAt)
  return d.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: e.allDay ? undefined : 'numeric',
    minute: e.allDay ? undefined : '2-digit',
  })
}

function capacityLabel(e: EventDto): string {
  if (e.capacity == null) return `${e.registeredCount} signed up`
  return `${e.registeredCount}/${e.capacity} seats`
}

export function EventsList() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['events'],
    queryFn: listEvents,
  })

  return (
    <div className="space-y-6">
      <PageHeader
        title="Events"
        description="Gatherings, services, classes, and registrations."
        actions={
          <Button>
            <CalendarPlus />
            New event
          </Button>
        }
      />

      {isLoading ? (
        <Card className="flex items-center justify-center gap-2 py-20 text-muted">
          <Spinner /> Loading events…
        </Card>
      ) : isError ? (
        <Card className="space-y-2 py-20 text-center">
          <p className="text-sm text-danger">Couldn’t load events.</p>
          <p className="text-xs text-dim">
            {error instanceof Error ? error.message : 'Is the API running on :3000?'}
          </p>
        </Card>
      ) : !data || data.length === 0 ? (
        <Card className="py-20 text-center text-sm text-muted">
          No events yet. Create your first event to get started.
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {data.map((e) => (
            <Link key={e.id} to={`/app/events/${e.id}`} className="group block">
              <Card className="h-full p-5 transition-colors group-hover:bg-card-hover">
                <div className="flex items-start justify-between gap-3">
                  <div className="grid size-10 shrink-0 place-items-center rounded-lg bg-card text-secondary">
                    <CalendarDays className="size-5" />
                  </div>
                  <Badge variant={kindVariant[e.kind] ?? 'neutral'} className="capitalize">
                    {e.kind}
                  </Badge>
                </div>
                <h2 className="mt-4 font-display text-lg font-semibold tracking-tight text-fg">
                  {e.name}
                </h2>
                <p className="mt-1 text-sm text-muted">{formatWhen(e)}</p>
                <div className="mt-4 space-y-1.5 text-xs text-dim">
                  {(e.locationText || e.siteName) && (
                    <div className="flex items-center gap-1.5">
                      <MapPin className="size-3.5" />
                      <span className="truncate">{e.locationText ?? e.siteName}</span>
                    </div>
                  )}
                  {e.registrationEnabled && (
                    <div className="flex items-center gap-1.5">
                      <Users className="size-3.5" />
                      <span>{capacityLabel(e)}</span>
                      {e.waitlistedCount > 0 && (
                        <span className="text-warning">· {e.waitlistedCount} waitlisted</span>
                      )}
                    </div>
                  )}
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
